/**
 * 官方教程画布（P1 C-2a）：一张预置示例，讲清"从一个想法到一条成片"的跨节点流转。
 *
 * 流程按创作的真实逻辑排列（2026-09-30 用户校正）：
 *   剧本/想法 → 角色资产 → 3D 导演台（站位/动作/机位）
 *     → 场景视觉图（场景资产）┐
 *     → 音色配音（音频资产）  ┴→ 分镜1 / 分镜2 …（可派生 N 个）→ 时间线/合成成片
 * 用分镜 1/2 两条分支 + 角色/场景"可派生 N 个"的提示，说明每个环节都能展开成多节点。
 *
 * 导出前用 validateCanvasDoc 兜底（测试断言合法），避免教程自带悬空边误导用户。
 */
import {
  CANVAS_SCHEMA_VERSION,
  type CanvasDoc,
} from '../../model/canvas';

export const TUTORIAL_CANVAS_ID = 'canvas-tutorial';

/** 生成教程画布（每次返回新对象，载入后可自由编辑不影响源数据） */
export function createTutorialCanvas(): CanvasDoc {
  return {
    schemaVersion: CANVAS_SCHEMA_VERSION,
    id: TUTORIAL_CANVAS_ID,
    title: '官方教程 · 从一个想法到一条成片',
    updatedAt: new Date().toISOString(),
    nodes: [
      {
        id: 'n1',
        kind: 'text',
        mode: 'source',
        title: '① 剧本 / 想法',
        position: { x: 40, y: 200 },
        payload: { text: '一句话创意，例如"雨夜，主角在巷口回眸"。后续所有节点由此展开。' },
      },
      {
        id: 'n2',
        kind: 'subject',
        mode: 'source',
        title: '② 角色资产',
        position: { x: 300, y: 120 },
        payload: { hint: '角色资产视图（不是场景）。一个剧本可派生 N 个角色。' },
      },
      {
        id: 'n3',
        kind: 'director',
        mode: 'source',
        title: '③ 3D 导演台',
        position: { x: 560, y: 120 },
        payload: { hint: '按剧本场景需要，摆角色站位 / 动作 / 机位等空间信息。' },
      },
      {
        id: 'n4',
        kind: 'image',
        mode: 'generator',
        title: '④ 场景视觉图',
        position: { x: 820, y: 40 },
        params: {
          model: 'demo-image',
          capability: 'textToImage',
          aspectRatio: '16:9',
          resolution: '1024',
          durationSec: 0,
          count: 1,
          prompt: '依据 @@<n3> 导演台的机位，生成场景视觉图（场景资产）。',
          enhancePrompt: true,
        },
      },
      {
        id: 'n5',
        kind: 'audio',
        mode: 'generator',
        title: '⑤ 音色配音',
        position: { x: 820, y: 300 },
        params: {
          model: 'demo-tts',
          capability: 'tts',
          aspectRatio: '16:9',
          resolution: '1024',
          durationSec: 0,
          count: 1,
          prompt: '把 @@<n1> 剧本里的台词，按角色音色配音成音频资产。',
          enhancePrompt: false,
        },
      },
      {
        id: 'n6',
        kind: 'video',
        mode: 'generator',
        title: '⑥ 分镜 1',
        position: { x: 1100, y: 120 },
        params: {
          model: 'demo-video',
          capability: 'imageToVideo',
          aspectRatio: '16:9',
          resolution: '1080',
          durationSec: 5,
          count: 1,
          prompt: '让 @@<n4> 场景图动起来，配合 @@<n5> 配音。',
          enhancePrompt: false,
        },
      },
      {
        id: 'n7',
        kind: 'video',
        mode: 'generator',
        title: '⑦ 分镜 2',
        position: { x: 1100, y: 340 },
        params: {
          model: 'demo-video',
          capability: 'imageToVideo',
          aspectRatio: '16:9',
          resolution: '1080',
          durationSec: 5,
          count: 1,
          prompt: '同一场景的另一条分镜（演示：可派生 N 个分镜）。',
          enhancePrompt: false,
        },
      },
      {
        id: 'n8',
        kind: 'timeline',
        mode: 'derived',
        title: '⑧ 时间线 / 成片',
        position: { x: 1380, y: 220 },
        payload: { hint: '把各分镜片段汇到时间线，进入剪辑合成。' },
      },
    ],
    edges: [
      { id: 'e1', from: 'n1', to: 'n2' },
      { id: 'e2', from: 'n2', to: 'n3' },
      { id: 'e3', from: 'n3', to: 'n4' },
      { id: 'e4', from: 'n1', to: 'n5' },
      { id: 'e5', from: 'n4', to: 'n6' },
      { id: 'e6', from: 'n5', to: 'n6' },
      { id: 'e7', from: 'n4', to: 'n7' },
      { id: 'e8', from: 'n6', to: 'n8' },
      { id: 'e9', from: 'n7', to: 'n8' },
    ],
    viewport: { x: 0, y: 0, zoom: 0.6 },
  };
}
